import React, { memo, useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Col, Collapse, Row } from 'reactstrap';
import { DealResponse } from '@business/entities/deal';
import { FieldType, SettingFieldResponse } from '@business/entities/settingFields';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { toMoment } from '@core/utils/time';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';

export interface DealCustomFieldsCardProps {
  deal: DealResponse;
}

// Some values are stored JSON-encoded (e.g. option-id arrays saved as the string "[47]").
function normalizeRaw(raw: any): any {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw;
      }
    }
  }
  return raw;
}

// Human-readable value for a custom field, or null when there is nothing worth showing.
function resolveValue(field: SettingFieldResponse, rawInput: any): string | null {
  const raw = normalizeRaw(rawInput);
  if (raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0)) return null;

  // Any field with an options list stores option ids (scalar or array) — map them to labels.
  if (field.values?.length) {
    const items = Array.isArray(raw) ? raw : [raw];
    const labels = items.map(r => field.values.find(v => String(v.objectId) === String(r))?.value).filter(Boolean);
    if (labels.length) return labels.join(', ');
    if (field.type === FieldType.dropdown || field.type === FieldType.singleOption || field.type === FieldType.multiOptions) {
      return items.map(String).join(', ');
    }
  }
  if (field.type === FieldType.datepicker) {
    const ts = Number(raw);
    return ts > 0 ? toMoment(ts).format('MM/DD/YYYY') : null;
  }
  if (field.type === FieldType.address) {
    if (typeof raw === 'object') {
      const parts = [raw.address, raw.city, raw.state, raw.postal].filter(Boolean);
      return parts.length ? parts.join(', ') : null;
    }
    return String(raw);
  }
  if (field.type === FieldType.file) {
    return typeof raw === 'object' ? raw?.fileName ?? null : null;
  }
  if (field.type === FieldType.html || field.type === FieldType.largetext) {
    const text = String(raw)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text || null;
  }
  return String(raw);
}

function buildRows(fields: SettingFieldResponse[], values: Record<string, any>) {
  return fields
    .map(field => ({ label: field.label, value: resolveValue(field, values[field.id]) }))
    .filter((row): row is { label: string; value: string } => !!row.value);
}

// Customer + contact custom fields captured at lead creation, shown above the progress bar.
// Renders nothing when neither side has any filled values.
export const DealCustomFieldsCard = memo((props: DealCustomFieldsCardProps) => {
  const { deal } = props;
  const settings = useSettings();
  // Collapsed by default — expand via the chevron.
  const [isOpen, setOpen] = useState(false);

  const { customerRows, contactRows } = useMemo(() => {
    const all = settings?.customFields ?? [];
    const orgValues = ((deal.organization as any)?.customFields ?? {}) as Record<string, any>;
    const contactValues = ((deal.contact as any)?.customFields ?? {}) as Record<string, any>;

    return {
      customerRows: buildRows(all.filter(f => f.for === 'organizations'), orgValues),
      contactRows: buildRows(all.filter(f => f.for === 'people'), contactValues),
    };
  }, [settings, deal.organization, deal.contact]);

  if (customerRows.length === 0 && contactRows.length === 0) return null;

  const renderSection = (title: string, rows: { label: string; value: string }[]) =>
    rows.length > 0 && (
      <div className="mb-2">
        <div className="text-muted font-size-12 font-weight-medium mb-2">{title}</div>
        <Row>
          {rows.map(row => (
            <Col md={3} sm={6} key={`${title}-${row.label}`} className="mb-2">
              <div className="text-muted font-size-11">{row.label}</div>
              <div className="font-size-13">{row.value}</div>
            </Col>
          ))}
        </Row>
      </div>
    );

  return (
    <Card className="mb-3">
      <CardBody className="py-3">
        {/* Same collapsible header pattern as the Organization/Contact page cards. */}
        <CardTitle className="card-title_items">
          <div>Additional Information</div>
          <div className="action">
            <a onClick={() => setOpen(!isOpen)}>
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
          </div>
        </CardTitle>
        <Collapse isOpen={isOpen}>
          {renderSection(gcLabel('Organization'), customerRows)}
          {renderSection(gcLabel('Contact'), contactRows)}
        </Collapse>
      </CardBody>
    </Card>
  );
});
